/**
 * VolumeRow — Shows daily pickup volume (wasserij items) above the schedule grid
 */

import type { RouteListItem } from '../../lib/salesforce/types';

interface VolumeRowProps {
  routes: RouteListItem[];
  weekDates: string[];
}

export function VolumeRow({ routes, weekDates }: VolumeRowProps) {
  // Sum itemCountOphalen per day across all routes
  const dailyVolume = weekDates.map(date => {
    const dayRoutes = routes.filter(r => r.datum === date);
    const items = dayRoutes.reduce((sum, r) => sum + (r.itemCountOphalen || 0), 0);
    const routeCount = dayRoutes.length;
    // Levende route: som van de verwachte uren, rood als één route boven budget zit.
    const minuten = dayRoutes.reduce((sum, r) => sum + (r.verwachteDuurMinuten ?? 0), 0);
    const overBudget = dayRoutes.some(r => (r.verwachteDuurMinuten ?? 0) > (r.dagBudgetMinuten ?? 600));
    return { date, items, routeCount, minuten, overBudget };
  });

  const totalItems = dailyVolume.reduce((sum, d) => sum + d.items, 0);
  if (totalItems === 0 && routes.length === 0) return null;

  return (
    <tr className="border-b border-sky-200 bg-sky-50/60">
      {/* Label */}
      <td className="sticky left-0 z-10 bg-sky-50 p-3 border-r border-sky-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-sky-200 text-sky-800">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-sky-800">Volume</p>
            <p className="text-[10px] text-sky-600">{totalItems} items</p>
          </div>
        </div>
      </td>

      {/* Day cells */}
      {dailyVolume.map(({ date, items, routeCount, minuten, overBudget }) => (
        <td key={date} className="p-1.5 border-r border-sky-100 align-top">
          {(items > 0 || routeCount > 0) && (
            <div className="rounded-lg bg-sky-100 border border-sky-200 p-1.5 text-center">
              <div className="text-sm font-bold text-sky-800">{items}</div>
              <div className="text-[10px] text-sky-600">
                items &middot; {routeCount} route{routeCount !== 1 ? 's' : ''}
                {minuten > 0 && (
                  <span className={overBudget ? 'text-red-600 font-semibold' : undefined} title={overBudget ? 'Minstens één route zit boven het dagbudget' : 'Som van de verwachte routeduur'}>
                    {' '}&middot; ~{Math.round(minuten / 60)}u
                  </span>
                )}
              </div>
            </div>
          )}
        </td>
      ))}

      {/* Hours column — empty */}
      <td className="p-3 text-center text-sm text-sky-400">-</td>
    </tr>
  );
}
